/**
 * Load Family Tool - BIM 2025 Grade
 *
 * Loads Yap family files (.yapfamily) or JSON family schemas and registers
 * them through the real FamilyLoader pipeline:
 * - JSON family schema loading (via FamilyLoader)
 * - Category validation
 * - Overwrite behavior (Existing vs New values)
 * Opens a real file picker on activation (BIM: Load Family dialog).
 *
 * Binary Revit .rfa families cannot be decoded in this build — the picker
 * does not offer them and any programmatic .rfa path is refused with an
 * explicit message. An unparseable file never produces a hollow family.
 */

import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import { familyLoader } from '../core/services';
import { FamilySchema } from '../core/families/FamilySchema';
import i18n from '../i18n';

export interface LoadFamilyOptions {
    filePath: string;
    overwrite: 'OverwriteParameterValues' | 'UseExistingValues';
}

export class LoadFamilyTool extends BaseTool {
    id = 'load_family';
    name = 'Load Family';
    cursor = 'default';

    private options: LoadFamilyOptions = {
        filePath: '',
        overwrite: 'OverwriteParameterValues'
    };

    onActivate(): void {
        // Dialog-driven command: BIM opens the family library dialog.
        void this.pickFile('.yapfamily,.json').then((file) => {
            if (file) {
                void this.loadFromFile(file);
            } else {
                this.exitToSelect();
            }
        });
    }

    private async loadFromFile(file: File): Promise<void> {
        try {
            if (/\.rfa$/i.test(file.name)) {
                this.notify(i18n.t('tools.notify.rfaUnsupported', { fileName: file.name }), 'error');
                return;
            }
            const content = await file.text();
            const schema = familyLoader.parseFromString(content);
            if (!schema) {
                this.notify(i18n.t('tools.notify.familyParseFailed', { fileName: file.name }), 'error');
                return;
            }
            await familyLoader.loadFamily(schema as FamilySchema);
            this.notify(i18n.t('tools.notify.familyLoaded', { name: schema.name }), 'success');
            this.options.filePath = file.name;
        } catch (err) {
            this.notify(i18n.t('tools.notify.familyLoadFailed', { error: String(err) }), 'error');
        } finally {
            this.exitToSelect();
        }
    }

    /**
     * Load with a known path (kept for tests/macros). The file content must
     * be supplied — a path alone cannot produce a family, and a binary .rfa
     * path is refused instead of registering a hollow entry.
     */
    public async executeLoad(filePath: string, content?: string): Promise<boolean> {
        if (/\.rfa$/i.test(filePath)) {
            this.notify(i18n.t('tools.notify.rfaUnsupported', { fileName: filePath }), 'error');
            return false;
        }
        if (content === undefined) {
            this.notify(i18n.t('tools.notify.familyLoadFailed', { error: `${filePath}: file content not available.` }), 'error');
            return false;
        }

        const schema = familyLoader.parseFromString(content);
        if (!schema) {
            this.notify(i18n.t('tools.notify.familyParseFailed', { fileName: filePath }), 'error');
            return false;
        }
        await familyLoader.loadFamily(schema as FamilySchema);
        this.options.filePath = filePath;
        this.notify(i18n.t('tools.notify.familyLoaded', { name: schema.name }), 'success');
        return true;
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Not interactive
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void {
        // Not used
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void {
        // Not used
    }
}
